/* eslint-disable -- React Compiler rules (react-hooks v6) flagged by external linter are not enabled in this project's CRA toolchain (react-hooks 5.2.0); false positives on async setState in debounced effects */
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Crown, LogOut, Mail, Phone, CreditCard, CheckCircle2, Clock, User, CalendarDays, LogIn, LogOut as LogOutIcon, Trophy, Search, Plus, X, Save } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const CLUB_INFO = {
  "bsv-niners": { label: "BSV Niners Wels", type: "Pool Billard", color: "#10b981" },
  "dsv-niners": { label: "DSV Niners Wels", type: "E-Dart", color: "#06b6d4" },
  "sdv-niners": { label: "SDV Niners Wels", type: "Steel Dart", color: "#dc2626" },
  "soundtheatre": { label: "The Soundtheatre", type: "Musik/Events", color: "#8b5cf6" },
  "vip": { label: "Niners-VIP Club", type: "Exklusiv", color: "#d4af37" },
  "perchten": { label: "D´Wösa Höllen Viecha", type: "Perchten", color: "#f97316" },
};

const INTERVAL_LABELS = { monthly: "Monatlich", half_year: "Halbjährlich", year: "Jährlich" };

const LEAGUE_OPTIONS = [
  { id: "oecsv", name: "ÖCSV", apiSource: "oecsv" },
  { id: "oecsv-bl", name: "ÖCSV Bundesliga", apiSource: "oecsv-bl" },
  { id: "throwdarts", name: "Throwdarts", apiSource: "throwdarts" },
];

function LeagueTeamEditor({ leagueTeams, onSave }) {
  const [teams, setTeams] = useState(leagueTeams || []);
  const [editIndex, setEditIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState("");
  const [saving, setSaving] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!selectedLeague || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    const league = LEAGUE_OPTIONS.find(l => l.id === selectedLeague);
    if (!league) return;
    
    const timer = setTimeout(() => {
      axios.get(`${API}/api/${league.apiSource}/teams/search?q=${encodeURIComponent(searchQuery)}`)
        .then(r => { setSearchResults(r.data); setShowDropdown(true); })
        .catch(() => setSearchResults([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, selectedLeague]);

  const addNewEntry = () => {
    if (teams.length >= 5) {
      toast.error("Maximal 5 Liga-Einträge erlaubt");
      return;
    }
    setTeams([...teams, { league: "", team_name: "", division_id: "", division_name: "" }]);
    setEditIndex(teams.length);
    setSelectedLeague("");
    setSearchQuery("");
  };

  const selectTeam = (result) => {
    const newTeams = [...teams];
    newTeams[editIndex] = {
      league: selectedLeague,
      team_name: result.team_name,
      division_id: result.division_id,
      division_name: result.division_name || ""
    };
    setTeams(newTeams);
    setShowDropdown(false);
    setSearchQuery("");
    setEditIndex(null);
    setSelectedLeague("");
  };

  const removeEntry = (index) => {
    setTeams(teams.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const validTeams = teams.filter(t => t.league && t.team_name);
      await onSave(validTeams);
      setTeams(validTeams);
      toast.success("Liga-Teams gespeichert");
    } catch (e) {
      toast.error("Fehler beim Speichern");
    }
    setSaving(false);
  };

  const getLeagueName = (leagueId) => LEAGUE_OPTIONS.find(l => l.id === leagueId)?.name || leagueId;

  return (
    <div className="bg-card border border-white/5 p-6 mb-6" data-testid="league-teams-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
          <Trophy className="w-4 h-4" /> Meine Liga-Teams
        </h2>
        {teams.length < 5 && editIndex === null && (
          <button onClick={addNewEntry} data-testid="add-league-team-btn"
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase border border-white/10 hover:border-white/30 transition-colors">
            <Plus className="w-3 h-3" /> Hinzufügen
          </button>
        )}
      </div>

      {teams.length === 0 && editIndex === null && (
        <p className="text-sm text-muted-foreground">Keine Liga-Teams hinterlegt. Füge deine Teams hinzu, um deine Ligadaten zu verknüpfen.</p>
      )}

      <div className="space-y-3">
        {teams.map((entry, idx) => (
          <div key={`team-${entry.league}-${entry.team_name}-${idx}`} className="flex items-center gap-3 p-3 border border-white/10 bg-background/50" data-testid={`league-team-${idx}`}>
            {editIndex === idx ? (
              <div className="flex-1 space-y-3" ref={searchRef}>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Liga auswählen</label>
                  <select value={selectedLeague} onChange={e => { setSelectedLeague(e.target.value); setSearchQuery(""); }}
                    className="w-full h-9 px-3 text-sm bg-input/50 border border-white/10 text-foreground"
                    data-testid="league-select">
                    <option value="">-- Liga wählen --</option>
                    {LEAGUE_OPTIONS.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                {selectedLeague && (
                  <div className="relative">
                    <label className="text-xs text-muted-foreground mb-1 block">Team suchen</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input placeholder="z.B. Niners..." value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="pl-10 bg-input/50 border-white/10 rounded-none"
                        data-testid="team-search-input" />
                    </div>
                    {showDropdown && searchResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-card border border-white/10 shadow-xl max-h-48 overflow-y-auto">
                        {searchResults.map((r, i) => (
                          <button key={`${r.team_name}-${r.division_id}-${i}`} onClick={() => selectTeam(r)}
                            className="w-full text-left px-4 py-2 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                            <p className="text-sm font-semibold">{r.team_name}</p>
                            <p className="text-xs text-muted-foreground">{r.division_name}{r.region ? ` — ${r.region}` : ''}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <button onClick={() => { setEditIndex(null); if (!entry.team_name) removeEntry(idx); }}
                  className="text-xs text-muted-foreground hover:text-foreground">Abbrechen</button>
              </div>
            ) : (
              <>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">{entry.team_name}</p>
                  <p className="text-xs text-muted-foreground">{getLeagueName(entry.league)}{entry.division_name ? ` — ${entry.division_name}` : ''}</p>
                </div>
                <button onClick={() => removeEntry(idx)} className="p-1.5 text-destructive hover:bg-destructive/10"
                  data-testid={`remove-league-team-${idx}`}>
                  <X className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        ))}
      </div>

      {teams.length > 0 && editIndex === null && (
        <button onClick={handleSave} disabled={saving} data-testid="save-league-teams-btn"
          className="mt-4 flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-xs font-bold uppercase disabled:opacity-50">
          <Save className="w-3.5 h-3.5" /> {saving ? "Wird gespeichert..." : "Speichern"}
        </button>
      )}
    </div>
  );
}

export default function MemberPortalPage() {
  usePageTitle("Mein Bereich");
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [checkedIn, setCheckedIn] = useState(false);
  const [clubCheckins, setClubCheckins] = useState({});
  const navigate = useNavigate();

  const loadCheckinStatus = useCallback((approvedClubs) => {
    const token = localStorage.getItem("member_token");
    axios.get(`${API}/api/checkin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setCheckedIn(r.data.checked_in)).catch(() => {});
    approvedClubs.forEach(cid => {
      axios.get(`${API}/api/checkin/club/${cid}`).then(r => {
        setClubCheckins(prev => ({ ...prev, [cid]: r.data }));
      }).catch(() => {});
    });
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("member_token");
    if (!token) { navigate("/mitglied-login"); return; }
    axios.get(`${API}/api/members/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        setMember(r.data);
        setLoading(false);
        const approvedClubs = Object.entries(r.data.memberships || {}).filter(([, v]) => v.approved).map(([k]) => k);
        loadCheckinStatus(approvedClubs);
        if (approvedClubs.length > 0) {
          axios.get(`${API}/api/events`).then(er => {
            const now = new Date().toISOString().split("T")[0];
            const upcoming = er.data
              .filter(e => e.date >= now && approvedClubs.includes(e.club_id))
              .sort((a, b) => a.date.localeCompare(b.date))
              .slice(0, 8);
            setEvents(upcoming);
          }).catch(() => {});
        }
      })
      .catch(() => { localStorage.removeItem("member_token"); localStorage.removeItem("member"); navigate("/mitglied-login"); });
  }, [navigate, loadCheckinStatus]);

  const logout = () => {
    localStorage.removeItem("member_token");
    localStorage.removeItem("member");
    navigate("/");
  };

  if (loading || !member) return null;

  const memberships = member.memberships || {};
  const approvedClubs = Object.entries(memberships).filter(([, v]) => v.approved);
  const pendingClubs = Object.entries(memberships).filter(([, v]) => v.requested && !v.approved);

  return (
    <div className="min-h-[80vh] px-4 md:px-8 py-10 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>
            Mein Bereich
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Willkommen zurück, {member.name}!</p>
        </div>
        <button onClick={logout} data-testid="member-logout-btn"
          className="flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground hover:text-foreground border border-white/10 hover:border-white/20 transition-colors">
          <LogOut className="w-4 h-4" /> Abmelden
        </button>
      </div>

      {/* Personal Info Card */}
      <div className="bg-card border border-white/5 p-6 mb-6" data-testid="member-info-card">
        <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">Persönliche Daten</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <User className="w-4 h-4 text-primary flex-shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">Name</p>
              <p className="text-sm font-medium" data-testid="member-name">{member.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Mail className="w-4 h-4 text-primary flex-shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">E-Mail</p>
              <p className="text-sm font-medium" data-testid="member-email">{member.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Phone className="w-4 h-4 text-primary flex-shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">Telefon</p>
              <p className="text-sm font-medium" data-testid="member-phone">{member.phone}</p>
            </div>
          </div>
          {member.bar_id && (
            <div className="flex items-center gap-3">
              <CreditCard className="w-4 h-4 text-primary flex-shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">Bar-ID</p>
                <p className="text-sm font-bold tracking-wider" data-testid="member-bar-id">{member.bar_id}</p>
              </div>
            </div>
          )}
          {member.gender && (
            <div className="flex items-center gap-3">
              <User className="w-4 h-4 text-primary flex-shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">Geschlecht</p>
                <p className="text-sm font-medium" data-testid="member-gender">
                  {member.gender === "male" ? "Männlich" : member.gender === "female" ? "Weiblich" : "Divers"}
                </p>
              </div>
            </div>
          )}
          {member.birthdate && (
            <div className="flex items-center gap-3">
              <CalendarDays className="w-4 h-4 text-primary flex-shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">Geburtsdatum</p>
                <p className="text-sm font-medium" data-testid="member-birthdate">
                  {new Date(member.birthdate).toLocaleDateString("de-AT", { day: "2-digit", month: "2-digit", year: "numeric" })}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* League Teams */}
      <LeagueTeamEditor 
        leagueTeams={member.league_teams || []}
        onSave={async (teams) => {
          const token = localStorage.getItem("member_token");
          await axios.put(`${API}/api/members/me/league-teams`, teams, { 
            headers: { Authorization: `Bearer ${token}` } 
          });
          setMember(prev => ({ ...prev, league_teams: teams }));
        }}
      />

      {/* Check-in / Check-out */}
      {approvedClubs.length > 0 && (
        <div className="bg-card border border-white/5 p-6 mb-6" data-testid="checkin-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Check-in</h2>
            <button onClick={async () => {
              const token = localStorage.getItem("member_token");
              const endpoint = checkedIn ? "checkout" : "checkin";
              await axios.post(`${API}/api/${endpoint}`, {}, { headers: { Authorization: `Bearer ${token}` } });
              setCheckedIn(!checkedIn);
              loadCheckinStatus(approvedClubs.map(([k]) => k));
            }} data-testid="checkin-toggle-btn"
              className={`flex items-center gap-2 px-5 py-2.5 text-sm font-bold uppercase transition-all ${checkedIn
                ? "bg-red-600/20 text-red-400 border border-red-600/30 hover:bg-red-600/30"
                : "bg-green-600 text-white hover:bg-green-700"}`}>
              {checkedIn ? <><LogOutIcon className="w-4 h-4" /> Auschecken</> : <><LogIn className="w-4 h-4" /> Einchecken</>}
            </button>
          </div>
          {checkedIn && <p className="text-xs text-green-500 mb-4">Du bist eingecheckt. Andere Mitglieder deiner Vereine können sehen, dass du da bist.</p>}
          {/* Show who's checked in per club */}
          {approvedClubs.map(([cid]) => {
            const club = CLUB_INFO[cid];
            const names = clubCheckins[cid] || [];
            if (names.length === 0) return null;
            return (
              <div key={cid} className="mt-3" data-testid={`checkin-list-${cid}`}>
                <p className="text-xs font-bold mb-1" style={{ color: club?.color || "#888" }}>{club?.label || cid}</p>
                <div className="flex flex-wrap gap-1.5">
                  {names.map((n, i) => (
                    <Badge key={`checkin-${n.name}`} variant="outline" className="text-[10px]">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5 inline-block" />{n.name}
                    </Badge>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Active Memberships */}
      {approvedClubs.length > 0 && (
        <div className="mb-6" data-testid="approved-clubs-section">
          <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">Aktive Mitgliedschaften</h2>
          <div className="space-y-3">
            {approvedClubs.map(([cid, info]) => {
              const club = CLUB_INFO[cid] || { label: cid, type: "", color: "#888" };
              return (
                <div key={cid} className="bg-card border border-white/5 p-4 flex items-center gap-4" data-testid={`club-${cid}`}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: club.color + "20", border: `2px solid ${club.color}` }}>
                    <CheckCircle2 className="w-5 h-5" style={{ color: club.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm">{club.label}</p>
                    <p className="text-xs text-muted-foreground">{club.type}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {info.fee > 0 ? (
                      <>
                        <p className="text-sm font-bold" style={{ color: club.color }}>{info.fee.toFixed(0)} €</p>
                        <p className="text-[10px] text-muted-foreground">{INTERVAL_LABELS[info.fee_interval] || info.fee_interval}</p>
                      </>
                    ) : (
                      <Badge variant="outline" className="text-[10px]">Kostenlos</Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Pending Memberships */}
      {pendingClubs.length > 0 && (
        <div className="mb-6" data-testid="pending-clubs-section">
          <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">Ausstehende Anträge</h2>
          <div className="space-y-3">
            {pendingClubs.map(([cid]) => {
              const club = CLUB_INFO[cid] || { label: cid, type: "", color: "#888" };
              return (
                <div key={cid} className="bg-card border border-yellow-600/20 p-4 flex items-center gap-4" data-testid={`pending-${cid}`}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-yellow-600/10 border-2 border-yellow-600/40">
                    <Clock className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm">{club.label}</p>
                    <p className="text-xs text-muted-foreground">{club.type}</p>
                  </div>
                  <Badge className="bg-yellow-600/20 text-yellow-500 border-yellow-600/40 text-[10px]">Wird geprüft</Badge>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {approvedClubs.length === 0 && pendingClubs.length === 0 && (
        <div className="bg-card border border-white/5 p-8 text-center">
          <p className="text-muted-foreground text-sm">Keine Vereinsmitgliedschaften vorhanden.</p>
        </div>
      )}

      {/* Upcoming Events */}
      {events.length > 0 && (
        <div className="mb-6" data-testid="upcoming-events-section">
          <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">Kommende Vereins-Events</h2>
          <div className="space-y-2">
            {events.map(ev => {
              const club = CLUB_INFO[ev.club_id] || { label: ev.club_id, color: "#888" };
              const dateStr = new Date(ev.date).toLocaleDateString("de-AT", { weekday: "short", day: "2-digit", month: "short" });
              return (
                <Link key={ev.id} to={`/event/${ev.id}`} className="block" data-testid={`portal-event-${ev.id}`}>
                  <div className="bg-card border border-white/5 p-4 flex items-center gap-4 hover:border-white/20 transition-colors">
                    <div className="w-1 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: club.color }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold uppercase tracking-tight truncate" style={{ fontFamily: 'Oswald' }}>{ev.title}</p>
                      <p className="text-xs text-muted-foreground">
                        <CalendarDays className="w-3 h-3 inline mr-1" />{dateStr}{ev.time ? ` | ${ev.time} Uhr` : ''}
                      </p>
                    </div>
                    <Badge style={{ backgroundColor: club.color + "20", color: club.color, border: `1px solid ${club.color}40` }} className="text-[10px] font-bold shrink-0">
                      {club.label}
                    </Badge>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* VIP Badge */}
      {memberships["vip"]?.approved && (
        <div className="mt-6 bg-gradient-to-r from-[#d4af37]/10 to-transparent border border-[#d4af37]/20 p-5 flex items-center gap-4" data-testid="vip-badge">
          <Crown className="w-8 h-8 text-[#d4af37]" />
          <div>
            <p className="font-bold text-sm" style={{ color: '#d4af37' }}>Niners-VIP Mitglied</p>
            <p className="text-xs text-muted-foreground">Sie genießen exklusive VIP-Vorteile.</p>
          </div>
        </div>
      )}
    </div>
  );
}
